import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Mail, Phone, Calendar, Trash2, Check, MailOpen,
  ChevronDown, ChevronUp, Inbox
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch (_) {
    return iso;
  }
};

export const AdminMessagesView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contact-messages`, { withCredentials: true });
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to load contact messages:', err);
      toast.error(t('فشل تحميل الرسائل', 'Failed to load messages'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/read`, {}, { withCredentials: true });
      setMessages((arr) => arr.map((m) => (m.id === id ? { ...m, is_read: true } : m)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('حذف هذه الرسالة؟', 'Delete this message?'))) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${id}`, { withCredentials: true });
      setMessages((arr) => arr.filter((m) => m.id !== id));
      toast.success(t('تم الحذف', 'Deleted'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const toggleExpand = (m) => {
    setExpandedId((prev) => (prev === m.id ? null : m.id));
    if (!m.is_read) markAsRead(m.id);
  };

  const visible = filter === 'unread' ? messages.filter((m) => !m.is_read) : messages;
  const unreadCount = messages.filter((m) => !m.is_read).length;

  return (
    <div className="space-y-4" data-testid="admin-messages-view" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-[#2c3e50] flex items-center gap-2">
          <Inbox size={20} className="text-[#1e88e5]" />
          {t('رسائل التواصل', 'Contact Messages')}
          {unreadCount > 0 && (
            <span className="text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded-full">
              {unreadCount} {t('جديدة', 'new')}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-[#1e88e5] hover:bg-[#1976d2]' : ''}
            data-testid="filter-all"
          >
            {t('الكل', 'All')} ({messages.length})
          </Button>
          <Button
            type="button"
            size="sm"
            variant={filter === 'unread' ? 'default' : 'outline'}
            onClick={() => setFilter('unread')}
            className={filter === 'unread' ? 'bg-[#1e88e5] hover:bg-[#1976d2]' : ''}
            data-testid="filter-unread"
          >
            {t('غير مقروءة', 'Unread')} ({unreadCount})
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">{t('جاري التحميل…', 'Loading…')}</p>
      ) : visible.length === 0 ? (
        <Card className="p-10 text-center text-gray-500">
          <Inbox size={36} className="mx-auto mb-3 text-gray-300" />
          {filter === 'unread'
            ? t('لا توجد رسائل غير مقروءة', 'No unread messages')
            : t('لا توجد رسائل بعد', 'No messages yet')}
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((m) => {
            const expanded = expandedId === m.id;
            return (
              <Card
                key={m.id}
                className={`overflow-hidden transition-all ${!m.is_read ? 'border-r-4 border-[#1e88e5] bg-blue-50/40' : ''}`}
                data-testid={`message-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(m)}
                  className={`w-full px-4 py-3 flex items-center gap-3 ${isRTL ? 'text-right' : 'text-left'}`}
                  data-testid={`message-toggle-${m.id}`}
                >
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                    ${m.is_read ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-[#1e88e5]'}`}>
                    {m.is_read ? <MailOpen size={16} /> : <Mail size={16} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-bold ${m.is_read ? 'text-gray-700' : 'text-[#0d47a1]'}`}>
                        {m.full_name}
                      </span>
                      <span className="text-xs text-gray-500">{m.email}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      <strong>{m.subject || t('بدون موضوع', 'No subject')}</strong> — {m.message}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
                      <Calendar size={11} /> {formatDate(m.created_at)}
                    </p>
                  </div>
                  {expanded
                    ? <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />
                    : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                </button>

                {expanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3 bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-[#1565c0]" />
                        <a href={`mailto:${m.email}`} className="text-[#1565c0] hover:underline">{m.email}</a>
                      </div>
                      {m.phone && (
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-[#1565c0]" />
                          <a href={`tel:${m.phone}`} className="text-[#1565c0] hover:underline">{m.phone}</a>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-500">
                        <Calendar size={14} />
                        {formatDate(m.created_at)}
                      </div>
                    </div>

                    {m.subject && (
                      <div>
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-bold">
                          {t('الموضوع', 'Subject')}
                        </p>
                        <p className="text-sm font-bold text-[#0d47a1]">{m.subject}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-bold">
                        {t('الرسالة', 'Message')}
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{m.message}</p>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <a href={`mailto:${m.email}?subject=Re:%20${encodeURIComponent(m.subject || 'Tababa')}`}>
                        <Button size="sm" variant="outline" data-testid={`reply-${m.id}`}>
                          <Mail size={14} className={isRTL ? 'ml-1' : 'mr-1'} />
                          {t('الرد بالبريد', 'Reply by email')}
                        </Button>
                      </a>
                      {!m.is_read && (
                        <Button size="sm" variant="outline" onClick={() => markAsRead(m.id)} data-testid={`mark-read-${m.id}`}>
                          <Check size={14} className={isRTL ? 'ml-1' : 'mr-1'} />
                          {t('وضع علامة كمقروء', 'Mark as read')}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(m.id)}
                        data-testid={`delete-${m.id}`}
                      >
                        <Trash2 size={14} className={isRTL ? 'ml-1' : 'mr-1'} />
                        {t('حذف', 'Delete')}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminMessagesView;
